import {useSelector} from 'react-redux'
import Badge from '../../core/badge'
import {locationManager} from '../../../utils/locations'
import {extractHeaders, parseAuthorizationHeader} from '../../../utils/header'
import TextHelper from '../../../utils/text'
import authMethods from '../../../data/authMethods.json'
import {isValidJson} from '../../../utils/object'
import {lightTheme} from '@uiw/react-json-view/light'
import JsonView from '@uiw/react-json-view'
import CopyButton from '../../core/copyButton'
import classNames from 'classnames'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'

const JSON_TYPE = 'response-json-check'
const HTTP = 'http'

const MonitorDetails = ({isDarkMode}) => {
    const text = useNamespaceTranslations('monitoring')
    const conditionData = useNamespaceTranslations('condition-types')?.resource_conditions
    const resource = useSelector(state => state.monitoring.resource)
    const types = {
        'response-status-code-check': text.status_code,
        'response-body-keyword-check': text.keyword_text
    }

    const authorizationHeader = extractHeaders(resource).authorizationHeader
    const remainingHeaders = Object.entries(extractHeaders(resource).remainingHeaders).map(([key, value]) => ({key, value}))

    const authMethod = authMethods[parseAuthorizationHeader(authorizationHeader?.Authorization)?.type]
    const bearerToken = parseAuthorizationHeader(authorizationHeader?.Authorization)?.token
    const username = parseAuthorizationHeader(authorizationHeader?.Authorization)?.username
    const password = parseAuthorizationHeader(authorizationHeader?.Authorization)?.password?.replace(/./g, '*')

    const getConditionDetails = (condition) => {
        const conditionKey = condition?.type !== JSON_TYPE ? `${types[condition?.type]}: ` : condition?.key
        const conditionOperator = Object.values(conditionData?.json_key_value_check?.values)?.find(pair => pair.value === condition?.operator)?.label.toLowerCase()
        const conditionValue = Array.isArray(condition?.values) ? condition.values.join(', ') : condition.values

        return {
            key: conditionKey,
            operator: conditionOperator,
            value: conditionValue
        }
    }

    const getPeriodDuration = minutes => {
        return minutes === 0 ? text.immediately : minutes === 1 ? `${minutes} ${text.minute_text}` : `${minutes} ${text.minutes_text}`
    }

    const getMonitorData = (data) => {
        const resourceData = !isValidJson(data) ? data.replace(/\n/g, '').trim() : JSON.parse(data)
        return (
            <>
                {isValidJson(data) ?
                    <JsonView
                        value={resourceData}
                        style={lightTheme}
                        displayObjectSize={false}
                        enableClipboard={false}
                        className='resource-json-view'
                        displayDataTypes={false}
                    /> : <span>
                        {resourceData}
                    </span>}
                <CopyButton text={JSON.stringify(resourceData)}/>
            </>
        )
    }

    return (
        <div className={classNames('monitor-details', {
            dark: isDarkMode
        })}>
            <div className="monitor-details-box">
                <p className="box-title">{text.target}</p>
                <span className="box-body">
                    {resource?.type === 'http' && <Badge color="secondary">{resource?.method}</Badge>}
                    {resource?.type === 'http' ? <a className="url" href={resource?.url} target='_blank'>{resource?.url}</a> : resource?.host}
                </span>
            </div>
            <div className="monitor-details-box">
                <p className="box-title">{text.type}</p>
                <span className="box-body">{resource?.type?.toUpperCase()}</span>
            </div>
            {resource?.port && (
                <div className="monitor-details-box">
                    <p className="box-title">{text.port}</p>
                    <span className="box-body">{resource?.port}</span>
                </div>
            )}
            <div className="monitor-details-box">
                <p className="box-title">{text.locations}</p>
                <span className="box-body">
                    {resource?.regions?.map((item, index) => (
                        <span className="box-body" key={index}>
                            <span className="region-flag">
                                {locationManager.allRegions[item].icon}
                            </span>
                            {locationManager.allRegions[item].location}
                        </span>
                    ))}
                </span>
            </div>
            {Object.values(authorizationHeader)?.length > 0 && (
                <div className="monitor-details-box">
                    <p className="box-title">{text.authorization}</p>
                    <span className="box-body">{text.method} {authMethod?.label}</span>
                    {authMethod?.value === authMethods?.basic_auth?.value ?
                        <>
                            <span className="box-body">{text.username} {username}</span>
                            <span className="box-body">{text.password} {password}</span>
                        </> :
                        <span className="box-body" title={bearerToken}>{text.token} {TextHelper.textLengthChecker(bearerToken, 20)}</span>
                    }
                </div>
            )}
            {remainingHeaders?.length > 0 && (
                <div className="monitor-details-box">
                    <p className="box-title">{text.headers}</p>
                    {remainingHeaders?.map((header, index) => {
                        return (
                            <span className="box-body" key={index}>{`${header?.key}: ${header?.value}`}</span>
                        )
                    })}
                </div>
            )}
            <div className="monitor-details-box">
                <p className="box-title">{text.grace_period}</p>
                <span className="box-body">{getPeriodDuration(resource?.settings?.period?.grace)}</span>
            </div>
            <div className="monitor-details-box">
                <p className="box-title">{text.recovery_period}</p>
                <span className="box-body">{getPeriodDuration(resource?.settings?.period?.recovery)}</span>
            </div>
            {resource?.settings?.maintenance?.cron_expression && (
                <div className="monitor-details-box">
                    <p className="box-title">{text.maintenance}</p>
                    <span className="box-body">{`${text.cron_expression}: `}{resource?.settings?.maintenance?.cron_expression}</span>
                    <span className="box-body">{`${text.duration}: `}{getPeriodDuration(resource?.settings?.maintenance?.duration)}</span>
                </div>
            )}
            {resource?.conditions?.map((condition, index) => {
                const {key, operator, value} = getConditionDetails(condition)
                return <div className="monitor-details-box" key={index}>
                    <p className="box-title">{text.conditions}</p>
                    <span className="box-body"><span>{key} {operator} {value}</span></span>
                </div>
            })}
            {resource?.data && (
                <div className="monitor-details-box resource-data">
                    <p className="box-title">{text.data}</p>
                    <span className="box-body">{getMonitorData(resource.data)}
                    </span>
                </div>
            )}
            {
                resource?.type === HTTP && <>
                    <div className="monitor-details-box">
                        <p className="box-title">{text.skip_ssl_validation_text}</p>
                        <span className="box-body"><Badge color={resource.settings.skip_ssl_error ? text.warning_status : text.success_status}>{resource.settings.skip_ssl_error ? text.disabled_status : text.enabled_status}</Badge></span>
                    </div>
                    <div className="monitor-details-box">
                        <p className="box-title">{text.follow_redirect_text}</p>
                        <span className="box-body"><Badge color={resource.settings.not_follow_redirect ? text.warning_status : text.success_status}>{resource.settings.not_follow_redirect ? text.disabled_status : text.enabled_status}</Badge></span>
                    </div>
                </>
            }
        </div>
    )
}

export default MonitorDetails
